/**
 * Ticker-style exchange codes — PRESENTATION ONLY.
 *
 * Short, all caps, the way a venue is written on a terminal: NYSE, NASDAQ,
 * TSX, TSXV, LSE, HKEX. Not the legal name ("Bolsas y Mercados Españoles"),
 * and not a descriptive phrase — a row is scanned, not read.
 *
 * When a segment's qualifier would turn the code into a phrase, it collapses
 * to a dotted initial on the parent: PARIS.G (Euronext Growth Paris), OSLO.G,
 * SSE.C (Shanghai Connect). It stays a code, and it sorts beside its parent.
 *
 * This is not applied where the spelled-out tier IS the recognised form —
 * NASDAQ GS / GM / CM are how those boards are written everywhere, and
 * NASDAQ.G would be an invention. Recognition wins over consistency.
 *
 * Where a venue has no established acronym, the MIC IS the short name. A MIC
 * is already a short all-caps code, so falling back to it costs nothing and
 * beats inventing an abbreviation nobody would recognise. That is the case for
 * OOTC, SSME, WBAH and XSEC.
 *
 * STRICTLY front-end. Nothing here is sent, stored, or validated against: the
 * MIC stays the identity everywhere else, and the backend's ALLOWED_EXCHANGES
 * / EXCHANGE_TO_COUNTRY are untouched. Renaming an entry can only change
 * pixels, never a backtest.
 *
 * The official name is not lost — it stays searchable and is the row's
 * tooltip, so "TADAWUL" and "Saudi Exchange" both find XSAU.
 *
 * Two rules held while assigning these:
 *
 * 1. No collision across countries. Several obvious acronyms are already
 *    taken, and reusing one would point a user at the wrong hemisphere:
 *      NSE  is India (XNSE)      → Nagoya is NAG, not NSE
 *      BSE  is Bombay (XBOM)     → Budapest is BUD, not BSE
 *      PSE  is Manila (XPHS)     → Prague is PRA, not PSE
 *      PSX  is Pakistan (XKAR)   → Nasdaq PSX is NASDAQ PSX
 *      TSE  is Tokyo (XTKS)      → Toronto is TSX
 *      CSE  would be ambiguous   → Casablanca is CASA
 *      FSE  would be ambiguous   → Frankfurt is FWB, Fukuoka is FUK
 *
 * 2. Segments of one venue may share a code (NYSE/NYSE, AMEX/AMEX, SIX/SIX).
 *    They are the same exchange, and the MIC badge sits right beside the name.
 */
export const EXCHANGE_SHORT_NAMES: Readonly<Record<string, string>> = {
  // United States
  XNYS: 'NYSE',
  NYSE: 'NYSE',
  XNAS: 'NASDAQ',
  XNGS: 'NASDAQ GS',
  XNMS: 'NASDAQ GM',
  XNCM: 'NASDAQ CM',
  OTCM: 'OTC',
  XOTC: 'OTC LINK',
  PINX: 'OTC PINK',
  OOTC: 'OOTC',
  XASE: 'AMEX',
  AMEX: 'AMEX',
  ARCX: 'ARCA',
  XPSX: 'NASDAQ PSX',
  XCBO: 'CBOE',
  BATS: 'BZX',
  IEXG: 'IEX',
  // Canada
  XTSE: 'TSX',
  XTSX: 'TSXV',
  // Latin America
  XMEX: 'BMV',
  BVMF: 'B3',
  XSGO: 'BCS',
  XBOG: 'BVC',
  XLIM: 'BVL',
  // United Kingdom
  XLON: 'LSE',
  // Germany
  XETR: 'XETRA',
  XFRA: 'FWB',
  XBER: 'BER',
  // Euronext — no venue-level acronym is in common use, so the city carries it.
  XPAR: 'PARIS',
  ALXP: 'PARIS.G',
  XAMS: 'AMSTERDAM',
  XBRU: 'BRUSSELS',
  XDUB: 'DUBLIN',
  XLIS: 'LISBON',
  MERK: 'OSLO.G',
  // Switzerland
  XSWX: 'SIX',
  XVTX: 'SIX',
  // Italy
  MTAA: 'MTA',
  XMIL: 'MIL',
  // Spain
  BMEX: 'BME',
  XMAD: 'MAD',
  XMCE: 'SIBE',
  // Nordics
  XSTO: 'OMXSTO',
  SSME: 'SSME',
  XHEL: 'OMXHEX',
  XCSE: 'OMXCOP',
  XOSL: 'OSL',
  // Austria
  XWBO: 'VIE',
  WBAH: 'WBAH',
  // Central & Eastern Europe
  XATH: 'ATHEX',
  XWAR: 'GPW',
  XBUD: 'BUD',
  XPRA: 'PRA',
  // Turkey
  XIST: 'BIST',
  XEQY: 'BIST EQ',
  // Russia
  MISX: 'MOEX',
  RTSX: 'RTS',
  // Japan
  XTKS: 'TSE',
  XNGO: 'NAG',
  XFKA: 'FUK',
  XOSE: 'OSE',
  XJAS: 'JASDAQ',
  // Asia-Pacific
  XASX: 'ASX',
  XHKG: 'HKEX',
  XSES: 'SGX',
  XNZE: 'NZX',
  // China
  XSEC: 'XSEC',
  XSSC: 'SSE',
  XSHE: 'SZSE',
  XSHG: 'SSE.C',
  // Korea
  XKRX: 'KRX',
  XKOS: 'KOSDAQ',
  // Taiwan
  XTAI: 'TWSE',
  ROCO: 'TPEX',
  // Southeast Asia
  XIDX: 'IDX',
  XKLS: 'MYX',
  XBKK: 'SET',
  XMAI: 'MAI',
  XPHS: 'PSE',
  // India
  XBOM: 'BSE',
  XNSE: 'NSE',
  // South & West Asia
  XKAR: 'PSX',
  XTAE: 'TASE',
  // Africa
  XJSE: 'JSE',
  XCAS: 'CASA',
  XCAI: 'EGX',
  // Middle East
  DSMD: 'QSE',
  XKUW: 'KSE',
  XSAU: 'TADAWUL',
  XADS: 'ADX',
  XDFM: 'DFM',
  DIFX: 'NASDAQ DUBAI',
};

/**
 * The code to show for a MIC. Falls back to the MIC itself, which is already a
 * short all-caps code — a new exchange therefore renders correctly on the day
 * it appears, and the test is what keeps the map deliberate rather than this
 * fallback.
 */
export function exchangeShortName(code: string): string {
  return EXCHANGE_SHORT_NAMES[code] ?? code;
}
